import { LayerInfo } from '@/types';
import {
  ChoroplethLayerOptionType,
  LayerItem,
} from '@/types/modules/MapLayerTypes.type';
import { colors } from '@/assets/colors/colors';

const layerColors = {
  agLand: '#189e5d',
  reservoir: '#6bd3ff',
  river: '#229cd3',
  basin: '#bffe99',
  roundtable: '#03fcc6',
  ditchColor: '#614dff',
  waterDistrict: '#70017a',
  flowNodeColor: '#fcc203',
  smpColor: '#eb7434',
  wsrfColor: '#eb34e5',
  cwpErColor: '#f01313',
  erFocusAreaColor: '#2e6e5b',
};

export const choroplethColors = {
  // these colors are mapped in style section of the choropleth legend file by way of Vuetify variables.
  low: colors.choroplethLow,
  moderate: colors.choroplethMed,
  high: colors.choroplethHigh,
  extreme: colors.choroplethExtreme,
};

export enum mapLayerIds {
  CHOROPLETH_LAYER = 'CHOROPLETH_LAYER',
  AGRICULTURAL_LAND = 'agricultural_land',
  BASINS = 'basins_geom',
  BASINS_ROUNDTABLE = 'basins_ibcc',
  CWP_ER = 'cwp_er',
  ER_FOCUS_AREA = 'er_focus_areas',
  FLOW_TOOL_NODES = 'flow_tool_nodes',
  IRRIGATION_DITCHES = 'irrigation_ditches',
  RESERVOIRS = 'reservoirs',
  RIVERS = 'rivers_detail',
  SMP_LINE = 'smp_line',
  SMP_POLYGON = 'smp_polygon',
  WATER_DISTRICTS = 'water_districts',
  WSRF = 'wsrf',
}

export const layerData: Record<string, LayerInfo> = {
  agriculturalLand: {
    source: {
      id: mapLayerIds.AGRICULTURAL_LAND,
      fields: [
        'gid',
        'objectid',
        'cal_year',
        'div',
        'district',
        'parcel_id',
        'master_id',
        'crop_type',
        'crop_src',
        'perennial',
        'irrig_type',
        'acres',
        'sw_wdid1',
        'sw_wdid2',
        'sw_wdid3',
        'sw_wdid4',
        'sw_wdid5',
        'sw_wdid6',
        'sw_wdid7',
        'sw_wdid8',
        'sw_wdid9',
        'gw_type1',
        'gw_id1',
        'gw_class1',
        'gw_type2',
        'gw_id2',
        'gw_class2',
        'gw_type3',
        'gw_id3',
        'gw_class3',
        'gw_type4',
        'gw_id4',
        'gw_class4',
        'gw_type5',
        'gw_id5',
        'gw_class5',
        'gw_type6',
        'gw_id6',
        'gw_class6',
        'gw_type7',
        'gw_id7',
        'gw_class7',
        'gw_type8',
        'gw_id8',
        'gw_class8',
        'gw_type9',
        'gw_id9',
        'gw_class9',
        'gw_type10',
        'gw_id10',
        'gw_class10',
        'gw_type11',
        'gw_id11',
        'gw_class11',
        'gw_type12',
        'gw_id12',
        'gw_class12',
        'gw_type13',
        'gw_id13',
        'gw_class13',
        'gw_type14',
        'gw_id14',
        'gw_class14',
        'gw_type15',
        'gw_id15',
        'gw_class15',
        'gw_type16',
        'gw_id16',
        'gw_class16',
        'gw_type17',
        'gw_id17',
        'gw_class17',
        'gw_type18',
        'gw_id18',
        'gw_class18',
        'gw_type19',
        'gw_id19',
        'gw_class19',
        'gw_type20',
        'gw_id20',
        'gw_class20',
        'irr',
        'gwzone',
        'farm_unit',
        'comments',
        'shape_leng',
        'shape_area',
        'layer',
        'path',
      ],
      info: [
        { name: 'District', field: 'district' },
        { name: 'Irrigation Type', field: 'irrig_type' },
      ],
    },
    layer: {
      id: mapLayerIds.AGRICULTURAL_LAND,
      type: 'fill',
      source: mapLayerIds.AGRICULTURAL_LAND,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.agLand,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.85,
          0.7,
        ],
        'fill-outline-color': 'black',
      },
      layout: { visibility: 'none' },
    },
  },
  reservoirs: {
    source: {
      id: mapLayerIds.RESERVOIRS,
      fields: [
        'gid',
        'comid',
        'fdate',
        'resolution',
        'gnis_id',
        'gnis_name',
        'elevation',
        'reachcode',
        'ftype',
        'fcode',
        'edited',
        'ed_scale',
        'ed_by',
        'ed_date',
        'acres',
        'max_',
        'shape_leng',
        'shape_area',
      ],
      info: [
        { name: 'Reservoir Name', field: 'gnis_name' },
        { name: 'ID', field: 'gnis_id' },
      ],
    },
    layer: {
      id: mapLayerIds.RESERVOIRS,
      type: 'fill',
      source: mapLayerIds.RESERVOIRS,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.reservoir,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.9,
          0.5,
        ],
        'fill-outline-color': 'black',
      },
      layout: { visibility: 'none' },
    },
  },
  river: {
    source: {
      id: mapLayerIds.RIVERS,
      fields: [
        'gid',
        'ihabbsrf_i',
        'rr',
        'huc',
        'type',
        'pmile',
        'pname',
        'owname',
        'pnmcd',
        'ownmcd',
        'dsrr',
        'dshuc',
        'usdir',
        'lev',
        'j',
        'termid',
        'trmblv',
        'k',
      ],
      info: [
        { name: 'River Name', field: 'pname' },
        { name: 'Type', field: 'type' },
      ],
    },
    layer: {
      id: mapLayerIds.RIVERS,
      type: 'line',
      source: mapLayerIds.RIVERS,
      'source-layer': 'tile',
      paint: {
        'line-color': layerColors.river,
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          3.5,
          1.5,
        ],
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'none',
      },
    },
  },
  basins: {
    source: {
      id: mapLayerIds.BASINS,
      fields: ['gid', 'area', 'perimeter', 'hu6name', 'sq_miles'],
      info: [{ name: 'Basin Name', field: 'hu6name' }],
    },
    layer: {
      id: mapLayerIds.BASINS,
      type: 'fill',
      source: mapLayerIds.BASINS,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.basin,
        // 'fill-opacity': 0.5,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.7,
          0.3,
        ],
        'fill-outline-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'red',
          'black',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
  roundtableMap: {
    source: {
      id: mapLayerIds.BASINS_ROUNDTABLE,
      fields: [
        'gid',
        'objectid',
        'system',
        'area',
        'id',
        'name',
        'shape_leng',
        'label',
        'shape_area',
        'cwcb_web',
        'brt_web',
      ],
      info: [{ name: 'Roundtable Basin Name', field: 'label' }],
    },
    layer: {
      id: mapLayerIds.BASINS_ROUNDTABLE,
      type: 'fill',
      source: mapLayerIds.BASINS_ROUNDTABLE,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.roundtable,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.7,
          0.3,
        ],
        'fill-outline-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'red',
          'black',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
  flow_tool_nodes: {
    source: {
      id: mapLayerIds.FLOW_TOOL_NODES,
      fields: [
        'gid',
        'gagenum',
        'flowtoolno',
        'nodeabbrev',
        'source',
        'status',
        'latitude',
        'longitude',
        'elevation',
        'basinname',
        'periodofre',
        'coldwaterf',
        'warmwaterf',
        'plainsfish',
        'boating',
        'wetlandsri',
        'isf',
      ],
      info: [
        { name: 'Gauge Number', field: 'gagenum' },
        { name: 'Node Abbreviation', field: 'nodeabbrev' },
        { name: 'Basin Name', field: 'basinname' },
        { name: 'Source', field: 'source' },
        { name: 'Risk Score', field: 'risk_value' },
        { name: 'Risk Metric', field: 'risk_metric' },
      ],
    },
    layer: {
      id: mapLayerIds.FLOW_TOOL_NODES,
      type: 'circle',
      source: mapLayerIds.FLOW_TOOL_NODES,
      'source-layer': 'tile',
      paint: {
        'circle-color': layerColors.flowNodeColor,
        'circle-radius': 6,
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          6,
          2,
        ],
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'black',
          'white',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
  irrigation_ditches: {
    source: {
      id: mapLayerIds.IRRIGATION_DITCHES,
      fields: [
        'gid',
        'objectid',
        'name',
        'wdid',
        'division',
        'length_ft',
        'awc',
        'perm',
        'shape_leng',
      ],
      info: [
        { name: 'Ditch Name', field: 'name' },
        { name: 'Division', field: 'division' },
      ],
    },
    layer: {
      id: mapLayerIds.IRRIGATION_DITCHES,
      type: 'line',
      source: mapLayerIds.IRRIGATION_DITCHES,
      'source-layer': 'tile',
      paint: {
        'line-color': layerColors.ditchColor,
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          8,
          3.5,
        ],
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'none',
      },
    },
  },
  water_districts: {
    source: {
      id: mapLayerIds.WATER_DISTRICTS,
      fields: [
        'gid',
        'objectid',
        'district',
        'division',
        'basin',
        'name',
        'shape_leng',
        'shape_area',
      ],
      info: [
        { name: 'District Name', field: 'name' },
        { name: 'District', field: 'district' },
        { name: 'Basin', field: 'basin' },
      ],
    },
    layer: {
      id: mapLayerIds.WATER_DISTRICTS,
      type: 'fill',
      source: mapLayerIds.WATER_DISTRICTS,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.waterDistrict,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.85,
          0.7,
        ],
        'fill-outline-color': 'black',
      },
      layout: { visibility: 'none' },
    },
  },
  smpLine: {
    source: {
      id: mapLayerIds.SMP_LINE,
      fields: [
        'gid',
        'oid_',
        'name',
        'folderpath',
        'symbolid',
        'altmode',
        'base',
        'clamped',
        'extruded',
        'snippet',
        'popupinfo',
        'shape_leng',
        'basin',
        'river',
        'sponsor',
        'link',
      ],
      info: [
        { name: 'Name', field: 'name' },
        { name: 'Link', field: 'link' },
      ],
    },
    layer: {
      id: mapLayerIds.SMP_LINE,
      type: 'line',
      source: mapLayerIds.SMP_LINE,
      'source-layer': 'tile',
      paint: {
        'line-color': layerColors.smpColor,
        'line-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          8,
          3.5,
        ],
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
        visibility: 'none',
      },
    },
  },
  smpPolygon: {
    source: {
      id: mapLayerIds.SMP_POLYGON,
      fields: [
        'gid',
        'oid_',
        'name',
        'folderpath',
        'symbolid',
        'altmode',
        'base',
        'clamped',
        'extruded',
        'snippet',
        'popupinfo',
        'shape_leng',
        'shape_area',
        'basin',
        'river',
        'sponsor',
        'link',
      ],
      info: [
        { name: 'Name', field: 'name' },
        { name: 'Link', field: 'link' },
      ],
    },
    layer: {
      id: mapLayerIds.SMP_POLYGON,
      type: 'fill',
      source: mapLayerIds.SMP_POLYGON,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.smpColor,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.7,
          0.3,
        ],
        'fill-outline-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'red',
          'black',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
  wsrf: {
    source: {
      id: mapLayerIds.WSRF,
      fields: ['gid', 'codeid', 'wateractiv', 'longituded', 'latitudedd'],
      info: [
        { name: 'Code Id', field: 'codeid' },
        { name: 'Water Activity', field: 'wateractiv' },
      ],
    },
    layer: {
      id: mapLayerIds.WSRF,
      type: 'circle',
      source: mapLayerIds.WSRF,
      'source-layer': 'tile',
      paint: {
        'circle-color': layerColors.wsrfColor,
        'circle-radius': 6,
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          6,
          2,
        ],
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'black',
          'white',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
  cwpEr: {
    source: {
      id: mapLayerIds.CWP_ER,
      fields: [
        'gid',
        'f_',
        'applicant',
        'project_na',
        'project_to',
        'grant_requ',
        'approved_a',
        'date_appro',
        'grant_prog',
        'contract_p',
        'requested',
        'start_date',
        'end_date',
        'invoiced_t',
        'amount_rem',
        'percent_co',
        'last_progr',
        'next_progr',
        'wsrf',
        'wsrf_invoi',
        'wsrf_remai',
        'basin',
        'location',
        'project_ty',
        'sub_catego',
        'latitude',
        'longitude',
        'general_fu',
        'notes',
        'project_ma',
        'completed',
        'final_repo',
        'final_re_1',
        'final_re_2',
        'final_re_3',
        'final_re_4',
      ],
      info: [
        { name: 'Location', field: 'location' },
        { name: 'Applicant', field: 'applicant' },
        { name: 'Start Date', field: 'start_date' },
        { name: 'End Date', field: 'end_date' },
      ],
    },
    layer: {
      id: mapLayerIds.CWP_ER,
      type: 'circle',
      source: mapLayerIds.CWP_ER,
      'source-layer': 'tile',
      paint: {
        'circle-color': layerColors.cwpErColor,
        'circle-radius': 6,
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          6,
          2,
        ],
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'black',
          'white',
        ],
      },
    },
  },
  erFocusAreas: {
    source: {
      id: mapLayerIds.ER_FOCUS_AREA,
      fields: [
        'gid',
        'huc10',
        'name',
        'basinname',
        'fish',
        'wildlife',
        'rec_and_ec',
        'waterright',
        'importwild',
        'phys_env',
        'shape_leng',
        'shape_area',
      ],
      info: [
        { name: 'Name', field: 'name' },
        { name: 'Fish', field: 'fish' },
        { name: 'Wildlife', field: 'wildlife' },
        { name: 'Water Rights', field: 'waterright' },
        { name: 'Physical Environment', field: 'phys_env' },
      ],
    },
    layer: {
      id: mapLayerIds.ER_FOCUS_AREA,
      type: 'fill',
      source: mapLayerIds.ER_FOCUS_AREA,
      'source-layer': 'tile',
      paint: {
        'fill-color': layerColors.erFocusAreaColor,
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          0.7,
          0.3,
        ],
        'fill-outline-color': [
          'case',
          ['boolean', ['feature-state', 'click'], false],
          'red',
          'black',
        ],
      },
      layout: { visibility: 'none' },
    },
  },
};

export const layers: LayerItem[] = [
  {
    title: 'rivers',
    selected: false,
    icon: 'LegendLine',
    color: layerColors.river,
    layerInfo: layerData.river,
  },
  {
    title: 'basins',
    selected: false,
    icon: 'LegendCircle',
    color: layerColors.basin,
    layerInfo: layerData.basins,
    hidden: true,
  },
  {
    title: 'roundtableMap',
    selected: false,
    icon: 'LegendCircle',
    color: layerColors.roundtable,
    layerInfo: layerData.roundtableMap,
    hidden: true,
  },
  {
    title: 'reservoirs',
    selected: false,
    icon: 'LegendCircle',
    color: layerColors.reservoir,
    layerInfo: layerData.reservoirs,
    hidden: true,
  },
  {
    title: 'agriculturalLand',
    selected: false,
    icon: 'LegendCircle',
    color: layerColors.agLand,
    layerInfo: layerData.agriculturalLand,
    hidden: true,
  },
  {
    title: 'irrigationDitches',
    selected: false,
    icon: 'LegendLine',
    color: layerColors.ditchColor,
    layerInfo: layerData.irrigation_ditches,
    hidden: true,
  },
  {
    title: 'waterDistricts',
    selected: false,
    icon: 'LegendCircle',
    color: layerColors.waterDistrict,
    layerInfo: layerData.water_districts,
    hidden: true,
  },
  {
    title: 'flowToolNodes',
    selected: false,
    icon: 'LegendCircleSmall',
    color: layerColors.flowNodeColor,
    layerInfo: layerData.flow_tool_nodes,
    hidden: true, // this feature layer is excluded from the layer menu and comes on with each choropleth layer.
  },
];

export const hucFields = [
  'basin',
  'flow_tool_node_name',
  'huc_10_basin',
  'macro_attribute',
  'scenario',
  'risk_metric',
  'risk_metric_absolute',
  'risk_value',
  'id',
  'basin_id',
];

export const basinFields = [
  // OLD FIELDS
  // 'basin',
  // 'scenario',
  // 'layer',
  // 'value',
  // 'risk_value',
  // 'id',
  // 'basin_id',
  'gid',
  'basin_id',
  'basinround',
];

export const choroplethLayers: ChoroplethLayerOptionType[] = [
  // {
  //   id: 'none',
  //   layer: null,
  //   dataSource: 'huc',
  //   showNodes: false,
  //   value: 0,
  //   note: null,
  //   options: [],
  // },
  {
    id: 'agMIRisk',
    layer: 'agMIRisk',
    dataSource: 'basin',
    showNodes: false,
    note: '',
    value: 1,
    options: [],
  },
  // {
  //   id: 'acreage',
  //   layer: 'acreage',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 1,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'totalMIGap',
  //   layer: 'mi_gap',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 2,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'urbanization',
  //   layer: 'lost_acreage',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 3,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'totalAgDemand',
  //   layer: 'ag_demand',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 4,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'totalAgGap',
  //   layer: 'ag_gap',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 5,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'incAgGap',
  //   layer: 'inc_ag_gap',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 6,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  // {
  //   id: 'population',
  //   layer: 'population',
  //   dataSource: 'basin',
  //   showNodes: false,
  //   note: 'at the Basin level',
  //   value: 7,
  //   options: [
  //     { label: 'Business as Usual', value: 'a' },
  //     { label: 'Weak Economy', value: 'b' },
  //     { label: 'Cooperative Growth', value: 'c' },
  //     { label: 'Adaptive Innovation', value: 'd' },
  //     { label: 'Hot Growth', value: 'e' },
  //   ],
  // },
  {
    id: 'coldWaterFish',
    layer: 'Cold%20Water%20Fish',
    dataSource: 'huc',
    showNodes: true,
    note: 'at the HUC-12 level',
    value: 8,
    options: [
      { label: 'Business as Usual', value: 'a' },
      { label: 'Weak Economy', value: 'b' },
      { label: 'Cooperative Growth', value: 'c' },
      { label: 'Adaptive Innovation', value: 'd' },
      { label: 'Hot Growth', value: 'e' },
    ],
  },
  {
    id: 'warmWaterFish',
    layer: 'Warm%20Water%20Fish',
    dataSource: 'huc',
    showNodes: true,
    note: 'at the HUC-12 level',
    value: 9,
    options: [
      { label: 'Business as Usual', value: 'a' },
      { label: 'Weak Economy', value: 'b' },
      { label: 'Cooperative Growth', value: 'c' },
      { label: 'Adaptive Innovation', value: 'd' },
      { label: 'Hot Growth', value: 'e' },
    ],
  },
  {
    id: 'wetRiparian',
    layer: 'Wetlands%20Riparian',
    dataSource: 'huc',
    showNodes: true,
    note: 'at the HUC-12 level',
    value: 10,
    options: [
      { label: 'Business as Usual', value: 'a' },
      { label: 'Weak Economy', value: 'b' },
      { label: 'Cooperative Growth', value: 'c' },
      { label: 'Adaptive Innovation', value: 'd' },
      { label: 'Hot Growth', value: 'e' },
    ],
  },
  {
    id: 'plainsFish',
    note: '',
    layer: 'Plains%20Fish',
    dataSource: 'huc',
    showNodes: true,
    value: 12,
    options: [
      { label: 'Business as Usual', value: 'a' },
      { label: 'Weak Economy', value: 'b' },
      { label: 'Cooperative Growth', value: 'c' },
      { label: 'Adaptive Innovation', value: 'd' },
      { label: 'Hot Growth', value: 'e' },
    ],
  },
];
